import { useEffect, useMemo, useState } from "react";
import { FileSpreadsheet, FileText, X } from "lucide-react";
import * as XLSX from "xlsx";
import { financeService } from "../../../services/financeService";
import { useFinanceStore } from "../../../store/financeStore";
import { FunnelCard } from "./FunnelCard";
import type { DateRange, Funnel, FunnelSortKey, FunnelSyncFilter } from "./types";
import type { Period } from "../../funnel-detail/types";
import { formatCurrency } from "../company/utils";

interface FunnelsGridProps {
  dateRange: DateRange;
  period: Period;
  sortBy: FunnelSortKey;
  syncFilter: FunnelSyncFilter;
  onRegisterExport?: (handler: (() => void) | null) => void;
}

export const FunnelsGrid = ({
  dateRange,
  period,
  sortBy,
  syncFilter,
  onRegisterExport,
}: FunnelsGridProps) => {
  const funnels = useFinanceStore((state) => state.funnels);
  const setFunnels = useFinanceStore((state) => state.setFunnels);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    financeService.getFunnels(dateRange)
      .then((items) => {
        if (!mounted) return;
        setFunnels(items);
      })
      .catch(() => {
        if (!mounted) return;
        setFunnels([]);
        setError("No pudimos cargar los embudos de Finance.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [dateRange, period.id, setFunnels]);

  const visibleFunnels = useMemo(() => {
    const filtered = funnels.filter((funnel) => {
      const synced =
        funnel.metaStatus !== "unconfigured" ||
        funnel.matchedCampaignIds.length > 0 ||
        funnel.manualLogisticsConfigured;
      if (syncFilter === "synced") return synced;
      if (syncFilter === "unsynced") return !synced;
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (sortBy === "sales") {
        return Number(b.metrics.salesValue ?? b.metrics.salesCount ?? -1) - Number(a.metrics.salesValue ?? a.metrics.salesCount ?? -1);
      }
      if (sortBy === "cpa") {
        return Number(b.metrics.cpa ?? -1) - Number(a.metrics.cpa ?? -1);
      }
      return Number(b.metrics.profit ?? -Infinity) - Number(a.metrics.profit ?? -Infinity);
    });
  }, [funnels, sortBy, syncFilter]);

  useEffect(() => {
    onRegisterExport?.(() => setExportModalOpen(true));
    return () => onRegisterExport?.(null);
  }, [onRegisterExport]);

  if (error) {
    return (
      <div className="rounded-lg border border-red-100 bg-red-50 px-5 py-4 text-sm font-medium text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="w-full">
      {loading ? <p className="mb-4 text-sm text-gray-400">Cargando embudos...</p> : null}
      <div
        id="finance-funnels-grid-target"
        className="grid justify-start gap-6 [grid-template-columns:repeat(auto-fit,minmax(min(100%,320px),360px))]"
      >
        {visibleFunnels.map((funnel, index) => (
          <FunnelCard key={funnel.id} funnel={funnel} sequence={index + 1} />
        ))}
      </div>

      {visibleFunnels.length > 0 ? (
        <div className="flex justify-center py-8">
          <p className="text-xs text-gray-300">{visibleFunnels.length} embudos</p>
        </div>
      ) : null}

      <FunnelsExportModal
        open={exportModalOpen}
        funnels={visibleFunnels}
        period={period}
        onClose={() => setExportModalOpen(false)}
      />
    </div>
  );
};

function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, (char) => {
    if (char === "&") return "&amp;";
    if (char === "<") return "&lt;";
    if (char === ">") return "&gt;";
    if (char === '"') return "&quot;";
    return "&#039;";
  });
}

function exportFunnelsPdf(funnels: Funnel[], period: Period) {
  const rows = funnels
    .map((funnel) => {
      const synced =
        funnel.metaStatus !== "unconfigured" ||
        funnel.matchedCampaignIds.length > 0 ||
        funnel.manualLogisticsConfigured;
      return `
        <tr>
          <td>${escapeHtml(funnel.name)}</td>
          <td>${escapeHtml(funnel.productName)}</td>
          <td class="numeric">${escapeHtml(formatCurrency(funnel.metrics.profit ?? 0))}</td>
          <td class="numeric">${escapeHtml(formatCurrency(funnel.metrics.salesValue ?? 0))}</td>
          <td class="numeric">${escapeHtml(formatCurrency(funnel.metrics.cpa ?? 0))}</td>
          <td>${synced ? "Sincronizado" : "No sincronizado"}</td>
        </tr>
      `;
    })
    .join("");

  const html = `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Estado de embudos</title>
        <style>
          * { box-sizing: border-box; }
          body { margin: 0; background: #f3f4f6; color: #111827; font-family: Inter, ui-sans-serif, system-ui, sans-serif; }
          main { width: 900px; margin: 32px auto; background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; padding: 32px; }
          h1 { margin: 0; font-size: 24px; }
          .period { margin-top: 6px; color: #6b7280; font-size: 12px; }
          table { width: 100%; margin-top: 24px; border-collapse: collapse; font-size: 12px; }
          th { background: #f8fafc; color: #64748b; font-size: 10px; letter-spacing: .04em; text-transform: uppercase; }
          th, td { border-bottom: 1px solid #edf0f3; padding: 10px 8px; text-align: left; }
          .numeric { text-align: right; font-variant-numeric: tabular-nums; }
          @media print { body { background: #fff; } main { width: auto; margin: 0; border: 0; padding: 0; } @page { margin: 16mm; } }
        </style>
      </head>
      <body>
        <main>
          <h1>Estado actual de tus embudos</h1>
          <div class="period">${escapeHtml(period.label)}</div>
          <table>
            <thead>
              <tr>
                <th>Embudo</th>
                <th>Producto</th>
                <th class="numeric">Utilidad</th>
                <th class="numeric">Ventas</th>
                <th class="numeric">CPA</th>
                <th>Sincronización</th>
              </tr>
            </thead>
            <tbody>${rows || '<tr><td colspan="6">Sin embudos para este filtro.</td></tr>'}</tbody>
          </table>
        </main>
        <script>window.addEventListener("load", () => setTimeout(() => window.print(), 250));</script>
      </body>
    </html>
  `;

  const reportWindow = window.open("about:blank", "_blank");
  if (!reportWindow) return;
  reportWindow.document.write(html);
  reportWindow.document.close();
  reportWindow.focus();
}

function exportFunnelsXlsx(funnels: Funnel[], period: Period) {
  const rows = funnels.map((funnel) => {
    const synced =
      funnel.metaStatus !== "unconfigured" ||
      funnel.matchedCampaignIds.length > 0 ||
      funnel.manualLogisticsConfigured;

    return {
      Embudo: funnel.name,
      Producto: funnel.productName,
      Utilidad: funnel.metrics.profit ?? 0,
      Ventas: funnel.metrics.salesValue ?? 0,
      CPA: funnel.metrics.cpa ?? 0,
      "Campañas conectadas": funnel.matchedCampaignIds.length,
      "Logística manual": funnel.manualLogisticsConfigured ? "Sí" : "No",
      Sincronización: synced ? "Sincronizado" : "No sincronizado",
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(rows);
  worksheet["!cols"] = [
    { wch: 34 },
    { wch: 26 },
    { wch: 16 },
    { wch: 16 },
    { wch: 14 },
    { wch: 20 },
    { wch: 18 },
    { wch: 18 },
  ];
  worksheet["!autofilter"] = { ref: `A1:H${Math.max(rows.length + 1, 2)}` };

  const workbook = XLSX.utils.book_new();
  workbook.Props = {
    Title: "Estado de embudos",
    Subject: period.label,
    Author: "Fluxi Finance",
    CreatedDate: new Date(),
  };
  XLSX.utils.book_append_sheet(workbook, worksheet, "Estado de embudos");
  XLSX.writeFile(workbook, `estado-embudos-${period.id}.xlsx`, { compression: true });
}

function FunnelsExportModal({
  open,
  funnels,
  period,
  onClose,
}: {
  open: boolean;
  funnels: Funnel[];
  period: Period;
  onClose: () => void;
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/35 px-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Estado de embudos</h2>
            <p className="mt-1 text-sm text-gray-500">
              Descarga el informe visible para {period.label}.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-gray-400 transition hover:bg-gray-50 hover:text-gray-700"
            aria-label="Cerrar exportación"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid gap-3 px-6 py-5">
          <button
            type="button"
            onClick={() => {
              exportFunnelsPdf(funnels, period);
              onClose();
            }}
            className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white px-4 py-4 text-left shadow-sm transition hover:bg-gray-50"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 text-[#2b5ce6]">
              <FileText className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-sm font-bold text-gray-900">Descargar PDF</span>
              <span className="mt-0.5 block text-xs text-gray-500">Vista imprimible del estado de embudos.</span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              exportFunnelsXlsx(funnels, period);
              onClose();
            }}
            className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white px-4 py-4 text-left shadow-sm transition hover:bg-gray-50"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
              <FileSpreadsheet className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-sm font-bold text-gray-900">Descargar Excel</span>
              <span className="mt-0.5 block text-xs text-gray-500">Archivo .xlsx con columnas listas para filtrar.</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
